import { Pie, PieConfig } from '@ant-design/charts';
import { useLazyQuery } from '@apollo/client';
import {
  GET_INSTITUTIONS_PITCH_UPLOADED_STATS,
  InstitutionsPitchStatistics,
  PitchStatistics,
} from '@frontend/data-access';
import { Card, DatePicker } from 'antd';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

function PitchUploadedStats() {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const [data, setData] = useState<PitchStatistics[]>([]);
  const [date, setDate] = useState<dayjs.Dayjs>(dayjs());
  const [getSearchChallengeData, { loading }] = useLazyQuery<{
    getCompanyPitchStatistics: InstitutionsPitchStatistics[];
  }>(GET_INSTITUTIONS_PITCH_UPLOADED_STATS, {
    onCompleted: (data) => {
      const newData: PitchStatistics[] = [];
      const list = data.getCompanyPitchStatistics;
      if (list && list.length > 0) {
        const lastItem = list[list.length - 1];
        newData.push({ type: t('without_pitches'), count: lastItem.unpitched });
        newData.push({ type: t('with_pitches'), count: lastItem.pitched });
      }
      setData(newData);
    },
  });

  const monthNames = useMemo(
    () => [
      tcommon('Jan'),
      tcommon('Feb'),
      tcommon('Mar'),
      tcommon('Apr'),
      tcommon('May'),
      tcommon('Jun'),
      tcommon('Jul'),
      tcommon('Aug'),
      tcommon('Sep'),
      tcommon('Oct'),
      tcommon('Nov'),
      tcommon('Dec'),
    ],
    [tcommon]
  );

  useEffect(() => {
    getSearchChallengeData({
      variables: {
        endDate: date?.format('YYYY-MM'),
      },
    });
  }, [date, getSearchChallengeData]);

  const config: PieConfig = {
    data: data,
    radius: 0.75,
    legend: {
      color: {
        position: 'bottom',
        flipPage: false,
        layout: {
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    loading: loading,
    angleField: 'count',
    colorField: 'type',
    scale: {
      color: {
        range: ['#58AFFF', '#63E063'],
      },
    },
    interaction: {
      tooltip: {
        name: 'type',
        render: (event, { title, items }) => {
          return (
            <div>
              <div>{title}</div>
              <div>
                {t('count')}: {items[0].value}
              </div>
            </div>
          );
        },
      },
    },
    label: {
      position: 'outside',
      style: {
        fontFamily: 'Lufga',
        fill: 'rgba(0, 0, 0, 0.65)',
      },
      labelHeight: 28,
      text: (d) => {
        return `${d.type}\n${(
          (d.count / data.reduce((acc, curr) => acc + curr.count, 0)) *
          100
        ).toFixed(2)}%`;
      },
    },
    color: ({ type }) => {
      switch (type) {
        case 'Without pitches':
          return '#58AFFF';
        case 'With pitches':
          return '#63E063';
        default:
          return '#1890FF';
      }
    },
    xAxis: {
      label: {
        style: { fontFamily: 'Lufga' },
        autoHide: true,
        autoRotate: false,
        formatter: (v) => {
          const monthNames = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ];
          const monthIndex = parseInt(v.split('-')[1], 10) - 1;
          return monthNames[monthIndex];
        },
      },
    },
    yAxis: {
      label: {
        style: { fontFamily: 'Lufga' },
      },
    },
    meta: {
      type: {
        alias: 'Challenge',
      },
    },
  };

  const onChange = (date: dayjs.Dayjs | null, dateString: string) => {
    setDate(date);
  };

  const disabledDate = (current: dayjs.Dayjs) => {
    return current && current > dayjs().endOf('month');
  };

  return (
    <Card style={{ borderRadius: 20 }}>
      <div className="space-y-4">
        <div className="flex flex-row justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('companies_with_pitches')}
          </div>
          <div>
            <DatePicker
              style={{ width: '150px', borderRadius: 20 }}
              onChange={onChange}
              picker="month"
              value={date}
              disabledDate={disabledDate}
            />
          </div>
        </div>
        <Pie key="pitch-uploaded-stats" {...config} />
      </div>
    </Card>
  );
}

export default PitchUploadedStats;
